import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, BlueRule } from "@/components/primitives";
import { ArticleCard } from "@/components/cards";
import NewsletterSection from "@/components/NewsletterSection";

export default function Ideas() {
  const { category } = useParams();
  const [topics, setTopics] = useState([]);
  const [articles, setArticles] = useState(null);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get("/topics").then((r) => {
      setTopics(r.data);
      setCurrent(r.data.find((t) => t.slug === category) || null);
    });
  }, [category]);

  useEffect(() => {
    setArticles(null);
    const params = {};
    const name = current?.name;
    if (name) params.category = name;
    api.get("/articles", { params }).then((r) => setArticles(r.data));
  }, [category, current]);

  const title = current ? current.name : "Ideas";
  useSEO({
    title: `${title} | Impactful Mindset`,
    description: current ? current.description : "Practical ideas about mindset, discipline, resilience, leadership, relationships and intentional living.",
  });

  return (
    <Layout>
      <Section className="pt-20 pb-10">
        <Overline>{current ? "Topic" : "The Ideas Platform"}</Overline>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4 text-[#111827]">{title}</h1>
        <BlueRule className="mt-6" />
        <p className="text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">
          {current ? current.description : "Essays and practical ideas on how to think more clearly and live more intentionally."}
        </p>
      </Section>

      <Section className="pb-6">
        <div className="flex flex-wrap gap-2">
          <Link to="/ideas" className={`px-4 py-2 text-sm border transition-colors ${!category ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-slate-300 text-slate-700 hover:border-[#0F2C59]"}`}>All</Link>
          {topics.map((t) => (
            <Link key={t.slug} to={`/ideas/${t.slug}`} data-testid={`ideas-filter-${t.slug}`}
              className={`px-4 py-2 text-sm border transition-colors ${category === t.slug ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-slate-300 text-slate-700 hover:border-[#0F2C59]"}`}>
              {t.name}
            </Link>
          ))}
        </div>
      </Section>

      <Section className="pb-20">
        {articles === null ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="h-80 bg-slate-100 animate-pulse" />)}
          </div>
        ) : articles.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-slate-300">
            <p className="font-serif-display text-xl text-slate-500">No articles here yet.</p>
            <p className="text-sm text-slate-400 mt-2">New ideas are published regularly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        )}
      </Section>

      <NewsletterSection />
    </Layout>
  );
}
